import { useMutation } from '@apollo/client';
import ConfirmationModal from 'components/ConfirmationModal';
import { EmptyStateWithText } from 'components/EmptyState';
import AsideListItem from 'components/Layout/components/BaseDataAside/AsideListItem';
import { useContextBaseDataAside } from 'components/Layout/components/BaseDataAside/context';
import EntitySubMenu from 'components/Layout/components/BaseDataAside/EntitySubMenu';
import { BaseDataForm, employeeSubmenu } from 'components/Layout/components/BaseDataAside/submenu';
import { TabPane } from 'components/Layout/components/common/styled-components';
import { useContextLayout } from 'components/Layout/context';
import { useContextSnackbar } from 'components/Snackbar/context';
import routePaths from 'constants/route-paths';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { matchPath, useLocation } from 'react-router-dom';
import { EmployeeListData } from 'services/employees';
import { DELETE_EMPLOYEE, RESTORE_EMPLOYEE } from 'services/mutation/employees';
import { AvatarImageType } from 'utils/getAvatarImage';
import { byLastName } from 'utils/sortBy';

const BaseDataEmployeesAsideMenu = () => {
  const { t } = useTranslation();
  const tabPaneRef = useRef(null);
  const { setActiveUser, setActiveMenu } = useContextBaseDataAside();
  const location = useLocation();
  const history = useHistory();
  const { filteredEmployees, setShouldRefetch } = useContextLayout();
  const { showApiError } = useContextSnackbar();

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeListData | undefined>();

  const [deleteEmployee] = useMutation(DELETE_EMPLOYEE);
  const [restoreEmployee] = useMutation(RESTORE_EMPLOYEE);

  const userPaths = matchPath(location.pathname, {
    path: routePaths.DATA_EMPLOYEE,
  }) as { params: { userId: string } };

  const pathName = location.pathname;

  useEffect(() => {
    const params = {
      userId: userPaths?.params?.userId,
    } as { userId: string };

    if (params.userId) {
      setActiveUser(params.userId);
      if (pathName.includes(routePaths.DATA_ACCESS)) {
        setActiveMenu(BaseDataForm.SYSTEM_ACCESS);
      } else if (pathName.includes(routePaths.DATA_CONTRACT)) {
        setActiveMenu(BaseDataForm.CONTRACT);
      } else {
        setActiveMenu(BaseDataForm.GENERAL);
      }
    }
  }, [setActiveMenu, setActiveUser, pathName, userPaths]);

  const onDeleteOrRestore = (employee: EmployeeListData) => {
    const name = `${employee.name.title ? employee.name.title + ' ' : ''}${
      employee.name.firstFull
    } ${employee.name.last}`;
    if (employee.deletedAt) {
      setConfirmationText(t('employee_restoreConfirmation', { name }));
    } else {
      setConfirmationText(t('employee_deleteConfirmation', { name }));
    }
    setShowConfirmation(true);
    setSelectedEmployee(employee);
  };

  const handleClose = (response: boolean) => {
    setShowConfirmation(false);
    if (!response || !selectedEmployee) {
      setSelectedEmployee(undefined);
      return;
    }
    if (!selectedEmployee.deletedAt) {
      deleteEmployee({ variables: { id: selectedEmployee.id } })
        .then(() => {
          setShouldRefetch('deleted');
          history.push(routePaths.BASE_DATA);
        })
        .catch(showApiError);
    } else {
      restoreEmployee({ variables: { id: selectedEmployee.id } })
        .then(() => {
          setShouldRefetch('restored');
          history.push(routePaths.BASE_DATA);
        })
        .catch(showApiError);
    }
  };

  return (
    <>
      <TabPane ref={tabPaneRef}>
        <div className="aside-menu">
          <ul className="menu-nav py-0">
            {filteredEmployees.length ? (
              filteredEmployees.sort(byLastName).map((employee: EmployeeListData) => (
                <AsideListItem
                  key={employee.id}
                  id={employee.id}
                  label={`${employee.name.last}, ${
                    employee.name.title ? employee.name.title + ' ' : ''
                  }${employee.name.firstFull}`}
                  description={employee.user.role?.name || t('noRoleAssigned')}
                  gender={employee.name.gender}
                  avatarImage={
                    (employee.profilePictureUrl && { url: employee.profilePictureUrl }) || {
                      svg: AvatarImageType.DEFAULT,
                    }
                  }
                  warning={1}
                  tabPaneRef={tabPaneRef}
                  routePath={routePaths.DATA_EMPLOYEE}
                  showDelete={true}
                  isDeleted={!!employee.deletedAt}
                  onDelete={() => onDeleteOrRestore(employee)}
                >
                  <EntitySubMenu
                    menuItems={employeeSubmenu}
                    entityId={employee.id}
                    isInactive={!!employee.deletedAt}
                    routePath={routePaths.DATA_EMPLOYEE}
                  />
                </AsideListItem>
              ))
            ) : (
              <EmptyStateWithText text={t('no_results_for_current_settings')} verticalPositionTop />
            )}
          </ul>
        </div>
      </TabPane>
      <ConfirmationModal show={showConfirmation} text={confirmationText} onResponse={handleClose} />
    </>
  );
};

export default BaseDataEmployeesAsideMenu;
